import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { apiFetch } from '../../api/client';
import { withSetorScope } from '../../utils/dataIsolation';

import { formatCurrencyBr } from '../../utils/parseValorMoeda';

const formatCurrency = formatCurrencyBr;

function normalizarChaveItem(desc) {
  return String(desc || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/** Monta matriz item × contrato para contratos variáveis com itens. */
function montarMatrizItens(contratos) {
  const comItens = (contratos || []).filter(
    (c) => c?.isValorVariavel && Array.isArray(c.itensEfetivos) && c.itensEfetivos.length > 0
  );
  if (comItens.length < 1) return null;

  const chaves = new Map(); // chave -> label
  for (const c of comItens) {
    for (const it of c.itensEfetivos) {
      const k = it.chave || normalizarChaveItem(it.descricao);
      if (!k) continue;
      if (!chaves.has(k)) chaves.set(k, it.descricao || k);
    }
  }
  if (!chaves.size) return null;

  const linhas = [...chaves.entries()].map(([chave, label]) => {
    const celulas = comItens.map((c) => {
      const it = (c.itensEfetivos || []).find(
        (x) => (x.chave || normalizarChaveItem(x.descricao)) === chave
      );
      if (!it) return { ausente: true };
      const total =
        it.total != null
          ? Number(it.total)
          : (Number(it.quantidade) || 0) * (Number(it.valor_unitario) || 0);
      return {
        ausente: false,
        quantidade: it.quantidade,
        unidade: it.unidade,
        valorUnitario: it.valor_unitario,
        total,
      };
    });
    const totais = celulas.filter((x) => !x.ausente && x.total != null).map((x) => x.total);
    const menor = totais.length ? Math.min(...totais) : null;
    return { chave, label, celulas, menor };
  });

  return { contratos: comItens, linhas };
}

export default function ComparativoServicosUnidadesPanel({ scopeOpts }) {
  const [objetoBusca, setObjetoBusca] = useState('');
  const [objetoAtivo, setObjetoAtivo] = useState('');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [incluirEncerrados, setIncluirEncerrados] = useState(true);
  const [somenteVigentes, setSomenteVigentes] = useState(false);

  const buscar = async () => {
    const termo = objetoBusca.trim();
    if (termo.length < 2) {
      setErro('Digite ao menos 2 caracteres do objeto do contrato.');
      return;
    }
    setObjetoAtivo(termo);
    setLoading(true);
    setErro('');
    try {
      const qs = new URLSearchParams({
        objeto: termo,
        incluirEncerrados: incluirEncerrados ? '1' : '0',
        somenteVigentes: somenteVigentes ? '1' : '0',
      });
      const path = withSetorScope(`/api/contratos/comparativo/por-objeto?${qs}`, scopeOpts);
      const res = await apiFetch(path);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao buscar comparativo.');
      setResultado(data);
    } catch (e) {
      setErro(e.message);
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(21,101,192,0.15)',
        backgroundColor: 'rgba(255,255,255,0.92)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CompareArrowsIcon color="primary" fontSize="small" />
        <Typography variant="h6" fontWeight={700} color="primary">
          Comparativo entre unidades
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Informe o objeto do contrato para buscar em todas as unidades cadastradas e comparar o valor/mês.
        Contratos sem valor fixo com itens são comparados também item a item.
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
        <TextField
          size="small"
          label="Objeto do contrato"
          placeholder="Ex.: Limpeza hospitalar, Coleta de resíduos..."
          value={objetoBusca}
          onChange={(e) => setObjetoBusca(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && buscar()}
          fullWidth
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={buscar}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap', minWidth: 140 }}
        >
          Buscar
        </Button>
      </Stack>

      <Stack direction="row" flexWrap="wrap" spacing={2} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={incluirEncerrados}
              onChange={(e) => setIncluirEncerrados(e.target.checked)}
              size="small"
            />
          }
          label="Incluir encerrados"
        />
        <FormControlLabel
          control={
            <Switch
              checked={somenteVigentes}
              onChange={(e) => setSomenteVigentes(e.target.checked)}
              size="small"
            />
          }
          label="Somente vigentes"
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : !resultado ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
          Digite o objeto e clique em Buscar para comparar contratos entre unidades.
        </Typography>
      ) : resultado.contratos?.length === 0 ? (
        <Alert severity="warning">
          Nenhum contrato encontrado com objeto contendo &quot;{objetoAtivo}&quot;.
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {resultado.total} contrato(s) encontrado(s) para &quot;{objetoAtivo}&quot;
            {resultado.referenciaValorMes != null &&
              ` · Menor valor/mês: ${formatCurrency(resultado.referenciaValorMes)}`}
            {resultado.multiUnidade && ' · Todas as unidades'}
            {resultado.fiscalSetorial && ' · Seu setor'}
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Unidade</TableCell>
                  <TableCell>Nº contrato</TableCell>
                  <TableCell>Fornecedor</TableCell>
                  <TableCell>Objeto</TableCell>
                  <TableCell>Cód. serviço</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Fonte valor</TableCell>
                  <TableCell align="right">Valor/mês</TableCell>
                  <TableCell align="right">% vs mais barato</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultado.contratos.map((c) => (
                  <TableRow
                    key={c.id}
                    sx={{ bgcolor: c.ehMaisBarato ? 'rgba(76,175,80,0.08)' : undefined }}
                  >
                    <TableCell>{c.unidade || '—'}</TableCell>
                    <TableCell>{c.numeroContrato}</TableCell>
                    <TableCell>{c.empresa}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{c.objeto || '—'}</TableCell>
                    <TableCell>{c.codigoServico || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status === 'vigente' ? 'Vigente' : 'Encerrado'}
                        color={c.status === 'vigente' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          c.valorComparativoFonte === 'nota_fiscal'
                            ? `NF ${c.nfCompetencia || ''}`.trim()
                            : c.valorComparativoFonte === 'itens_contrato'
                              ? 'Itens contrato'
                              : c.valorComparativoFonte === 'rateio'
                                ? 'Rateio'
                                : c.valorComparativoFonte === 'contrato_matriz'
                                  ? 'Matriz (integral)'
                                  : c.valorComparativoFonte === 'contrato'
                                    ? 'Contrato (sem NF)'
                                    : 'Fixo'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(c.valorMesNumerico ?? c.valorMes)}</TableCell>
                    <TableCell align="right">
                      {c.percentualVsMaisBarato != null
                        ? `${c.percentualVsMaisBarato > 0 ? '+' : ''}${c.percentualVsMaisBarato}%`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {(() => {
            const matriz = montarMatrizItens(resultado.contratos);
            if (!matriz) return null;
            return (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                  Comparativo por itens (contratos sem valor fixo)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Itens alinhados pela descrição. Valor fixo permanece só na tabela acima.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        {matriz.contratos.map((c) => (
                          <TableCell key={c.id} align="right">
                            {c.unidade || '—'}
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {c.numeroContrato}
                              {c.itensNumeroAditivo != null ? ` · Ad. ${c.itensNumeroAditivo}` : ''}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matriz.linhas.map((linha) => (
                        <TableRow key={linha.chave}>
                          <TableCell sx={{ maxWidth: 240 }}>{linha.label}</TableCell>
                          {linha.celulas.map((cel, idx) => (
                            <TableCell
                              key={`${linha.chave}-${matriz.contratos[idx].id}`}
                              align="right"
                              sx={{
                                bgcolor:
                                  !cel.ausente &&
                                  linha.menor != null &&
                                  Number(cel.total) === Number(linha.menor)
                                    ? 'rgba(76,175,80,0.08)'
                                    : undefined,
                              }}
                            >
                              {cel.ausente ? (
                                '—'
                              ) : (
                                <>
                                  <Typography variant="body2" component="span">
                                    {formatCurrency(cel.total)}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {cel.quantidade} {cel.unidade || ''} × {formatCurrency(cel.valorUnitario)}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}
        </>
      )}
    </Paper>
  );
}
